import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from './firebase'

export const MAX_FEEDBACK_ATTACHMENTS = 4
const MAX_LONG_EDGE = 1600
// JPEG, not WebP: older iOS Safari can't encode WebP and silently returns PNG.
const QUALITY = 0.8

/** Re-encodes an image as a JPEG no larger than MAX_LONG_EDGE on its long side. */
export async function shrinkAttachment(blob: Blob): Promise<Blob> {
  const url = URL.createObjectURL(blob)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('Failed to read image'))
      el.src = url
    })
    const scale = Math.min(1, MAX_LONG_EDGE / Math.max(img.width, img.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.width * scale)
    canvas.height = Math.round(img.height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D context unavailable')
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), 'image/jpeg', QUALITY)
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Uploads each attachment under the sender's Storage prefix (so account
 * deletion sweeps them) and returns the download URLs of the ones that made it.
 */
export async function uploadFeedbackAttachments(
  uid: string,
  blobs: Blob[],
): Promise<{ urls: string[]; failed: number }> {
  const batch = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const results = await Promise.allSettled(
    blobs.slice(0, MAX_FEEDBACK_ATTACHMENTS).map(async (blob, i) => {
      const fileRef = ref(storage, `users/${uid}/feedback/${batch}-${i}.jpg`)
      await uploadBytes(fileRef, await shrinkAttachment(blob), { contentType: 'image/jpeg' })
      return getDownloadURL(fileRef)
    }),
  )
  const urls = results.flatMap(r => (r.status === 'fulfilled' ? [r.value] : []))
  return { urls, failed: results.length - urls.length }
}
